import React, { useEffect, useRef } from 'react';
import classNames from 'classnames';
import { CheckboxInput } from 'Views/Components/New/CheckboxInput';

interface PurchaseTermsAcceptanceProps {
	/** Heading above the terms, e.g. "Parking Terms and Conditions". */
	title: string;
	/** The terms themselves, as stored HTML. */
	termsHtml: string;
	accepted: boolean;
	onAcceptedChange: (accepted: boolean) => void;
	/**
	 * How many times the step's Continue button has been pressed with the box unticked. While this is
	 * above zero and {@link accepted} is false, the checkbox is boxed in red with a message, and each
	 * increment scrolls it into view and focuses it.
	 *
	 * <b>A count, not a flag.</b> Repeating a press has to be observable here — a boolean set twice
	 * to the same value re-renders nothing, so the second press would leave a customer who had
	 * scrolled away looking at an unchanged screen.
	 */
	promptCount: number;
	/** For the `data-test-id` on the box around the checkbox. */
	testId?: string;
}

/**
 * The terms-and-conditions step shared by the anytime-ticket, parking and voucher purchase flows.
 *
 * Continue is deliberately left enabled by every caller while the box is unticked, so the press can
 * land here as a visible error. Disabling it instead — which all three used to do — leaves a
 * customer with a dead button and nothing saying why, and the checkbox is often below the fold
 * because `.purchase-terms-step__content` is its own scroll region.
 */
export function PurchaseTermsAcceptance({
	title,
	termsHtml,
	accepted,
	onAcceptedChange,
	promptCount,
	testId,
}: PurchaseTermsAcceptanceProps) {
	const acceptRef = useRef<HTMLDivElement>(null);
	const checkboxRef = useRef<HTMLInputElement>(null);

	// Ticking the box clears the prompt without the caller having to reset its counter.
	const showError = promptCount > 0 && !accepted;

	// Keyed on the count rather than on showError so a second press re-scrolls; run in an effect
	// rather than in the caller's click handler so the scroll is measured after the red box has
	// rendered and made the element taller.
	useEffect(() => {
		if (promptCount === 0 || accepted) {
			return;
		}
		acceptRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		checkboxRef.current?.focus({ preventScroll: true });
		// accepted is read, not depended on: ticking the box should not re-scroll.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [promptCount]);

	return (
		<div className="purchase-terms-step">
			<h4 className="purchase-terms-step__title">{title}</h4>
			<div
				className="purchase-terms-step__content wysiwyg-content"
				dangerouslySetInnerHTML={{ __html: termsHtml }}
			/>
			<div
				ref={acceptRef}
				className={classNames(
					'flex-shrink-0 rounded transition-colors border-2 p-3',
					showError ? 'border-red-500 bg-red-50' : 'border-transparent',
				)}
				data-test-id={testId}
			>
				<CheckboxInput
					ref={checkboxRef}
					label="I have read and agree to the terms and conditions"
					checked={accepted}
					onChange={e => onAcceptedChange(e.target.checked)}
				/>
				{showError && (
					<p className="mt-2 mb-0 text-sm text-red-600" role="alert">
						Please accept the terms and conditions to continue.
					</p>
				)}
			</div>
		</div>
	);
}

export default PurchaseTermsAcceptance;
